/* Reglas configurables del Capacitador (clave/valor). */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Save, Sliders, CheckCircle2, AlertCircle, RefreshCw, Edit3, Power } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtChile(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Santiago' });
  } catch { return iso; }
}

export default function CapacitadorReglas() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [reglas, setReglas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // clave que se está editando
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null); // { type, msg }

  async function cargar() {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/capacitador/reglas`, { headers });
      setReglas(r.data || []);
    } finally { setLoading(false); }
  }

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, []);

  function startEdit(r) { setEditing(r.clave); setDraft(r.valor); setToast(null); }
  function cancelEdit() { setEditing(null); setDraft(''); }

  // Helpers para tipo bool
  const isTrue = (v) => ['true', '1', 'on', 'yes', 'si', 'sí'].includes(String(v ?? '').trim().toLowerCase());
  async function toggleBool(r) {
    const nuevo = isTrue(r.valor) ? 'false' : 'true';
    setSaving(true);
    setToast(null);
    try {
      await axios.put(`${API}/capacitador/reglas/${r.clave}`, { valor: nuevo }, { headers });
      setToast({ type: 'ok', msg: `Regla "${r.label}" ${nuevo === 'true' ? 'ACTIVADA' : 'DESACTIVADA'}.` });
      await cargar();
    } catch (e) {
      setToast({ type: 'err', msg: e.response?.data?.detail || 'No se pudo actualizar.' });
    } finally {
      setSaving(false);
      setTimeout(() => setToast(null), 4000);
    }
  }

  async function guardar(r) {
    setSaving(true);
    setToast(null);
    try {
      await axios.put(`${API}/capacitador/reglas/${r.clave}`, { valor: draft }, { headers });
      setToast({ type: 'ok', msg: `Regla "${r.label}" actualizada.` });
      setEditing(null);
      await cargar();
    } catch (e) {
      setToast({ type: 'err', msg: e.response?.data?.detail || 'No se pudo guardar.' });
    } finally {
      setSaving(false);
      setTimeout(() => setToast(null), 4000);
    }
  }

  return (
    <div className="px-6 py-6 max-w-5xl mx-auto" data-testid="capacitador-reglas-page">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-teal-50 border border-teal-200 flex items-center justify-center">
              <Sliders className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Reglas del Capacitador</h1>
              <p className="text-sm text-slate-500">Parámetros configurables que aplican a todas las sesiones.</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={cargar} data-testid="reglas-recargar">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Recargar
          </Button>
        </div>

        {toast && (
          <div className={`mb-4 rounded-lg border p-3 text-sm flex items-start gap-2 ${toast.type === 'ok' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-rose-50 border-rose-200 text-rose-700'}`} data-testid="reglas-toast">
            {toast.type === 'ok' ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />}
            <span>{toast.msg}</span>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          {loading && reglas.length === 0 ? (
            <p className="py-14 text-center text-sm text-slate-400">Cargando reglas...</p>
          ) : reglas.length === 0 ? (
            <p className="py-14 text-center text-sm text-slate-400">No hay reglas configuradas.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {reglas.map(r => (
                <li key={r.clave} className="p-5" data-testid={`regla-${r.clave}`}>
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-bold text-slate-900">{r.label}</h3>
                      {r.descripcion && <p className="text-xs text-slate-500 mt-0.5 leading-snug">{r.descripcion}</p>}
                      <p className="text-[10px] uppercase tracking-wider text-slate-400 mt-2">
                        Última edición: {fmtChile(r.updated_at)}{r.updated_by ? ` · ${r.updated_by}` : ''}
                      </p>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {r.tipo === 'bool' ? (
                        // ===== Toggle switch para reglas booleanas =====
                        <button
                          type="button"
                          onClick={() => toggleBool(r)}
                          disabled={saving}
                          className={`group inline-flex items-center gap-3 px-3 py-1.5 rounded-full border-2 transition-all ${
                            isTrue(r.valor)
                              ? 'bg-emerald-50 border-emerald-300 hover:bg-emerald-100'
                              : 'bg-slate-50 border-slate-300 hover:bg-slate-100'
                          }`}
                          data-testid={`regla-toggle-${r.clave}`}
                          aria-pressed={isTrue(r.valor)}
                        >
                          <span className={`relative inline-flex w-10 h-5 rounded-full transition-colors ${isTrue(r.valor) ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${isTrue(r.valor) ? 'left-[22px]' : 'left-0.5'}`} />
                          </span>
                          <span className={`text-xs font-bold uppercase tracking-wider ${isTrue(r.valor) ? 'text-emerald-700' : 'text-slate-500'}`}>
                            {isTrue(r.valor) ? 'Activa' : 'Inactiva'}
                          </span>
                          <Power className={`w-3.5 h-3.5 ${isTrue(r.valor) ? 'text-emerald-600' : 'text-slate-400'}`} />
                        </button>
                      ) : editing === r.clave ? (
                        <>
                          <Input
                            type={r.tipo === 'int' ? 'number' : r.tipo === 'time' ? 'time' : 'text'}
                            min={r.min}
                            max={r.max}
                            value={draft}
                            onChange={e => setDraft(e.target.value)}
                            className={`${r.tipo === 'text' ? 'w-80' : 'w-32 text-right'} font-semibold`}
                            placeholder={r.tipo === 'text' ? 'https://tystacore.cl' : ''}
                            data-testid={`regla-input-${r.clave}`}
                          />
                          <Button onClick={() => guardar(r)} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid={`regla-guardar-${r.clave}`}>
                            <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Guardando...' : 'Guardar'}
                          </Button>
                          <Button variant="ghost" onClick={cancelEdit} disabled={saving}>Cancelar</Button>
                        </>
                      ) : (
                        <>
                          <span className={`${r.tipo === 'text' ? 'text-sm font-mono text-slate-700 max-w-[260px] truncate' : 'text-2xl font-black text-teal-600 tabular-nums'}`}
                            data-testid={`regla-valor-${r.clave}`}>
                            {r.valor || (r.tipo === 'text' ? <em className="text-slate-400">(vacío)</em> : '—')}
                          </span>
                          <Button variant="outline" size="sm" onClick={() => startEdit(r)} data-testid={`regla-editar-${r.clave}`}>
                            <Edit3 className="w-4 h-4 mr-1.5" /> Editar
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <p className="text-[11px] text-slate-400 mt-4 text-center">
          Los cambios aplican de inmediato. Sesiones ya inscritas no se modifican: la regla solo bloquea nuevas inscripciones.
        </p>
      </div>
  );
}
